import React, { useState, useEffect, useRef } from "react";
import { ref, get, set, serverTimestamp, database, onValue } from "../firebase";
import {
  FiMoreVertical,
  FiShare2,
  FiDownload,
  FiTrash2,
  FiEdit,
  FiVolume2,
  FiVolumeX,
  FiFilm,
  FiSearch,
  FiX,
  FiGlobe,
  FiImage,
  FiVideo,
  FiCalendar,
  FiArrowLeft,
  FiClock,
  FiCheck,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useSearch } from "../SearchContext";
import { searchYouTubeVideos } from "./YoutubeAPI";
import { FaRupeeSign } from "react-icons/fa";

// Enhanced MediaModal for photos with Like, Comment, Share
const MediaModal = ({
  mediaItem,
  uploader,
  onClose,
  currentUser,
  onLike,
  onComment,
  likes,
  comments,
  hasLiked,
  onShare,
  photoTime,
  photoPoints,
  onPhotoViewAward,
  viewProgress,
  viewAwarded,
}) => {
  const [commentText, setCommentText] = useState("");
  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (commentText.trim()) {
      onComment(commentText);
      setCommentText("");
    }
  };
  // Timeline/progress logic
  useEffect(() => {
    if (!photoTime || viewAwarded) return;
    let interval = null;
    if (mediaItem && currentUser) {
      interval = setInterval(() => {
        onPhotoViewAward(mediaItem, false); // false = not force, just tick
      }, 1000);
    }
    return () => interval && clearInterval(interval);
  }, [mediaItem, currentUser, photoTime, viewAwarded, onPhotoViewAward]);
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn p-2 sm:p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-4 w-full max-w-3xl h-[90vh] sm:h-auto overflow-y-auto relative flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 p-2"
          aria-label="Close modal"
        >
          <FiX className="text-2xl sm:text-3xl" />
        </button>
        {photoTime > 0 && (
          <div className="w-full mb-4 flex flex-col items-center">
            <div className="flex items-center mb-2">
              <span className="text-xs sm:text-sm font-semibold text-blue-700 dark:text-blue-300 mr-2">
                Earn:
              </span>
              <span className="text-sm sm:text-lg font-bold text-green-600 dark:text-green-400 flex items-center">
                +{photoPoints} pts
                {viewAwarded && (
                  <FiCheck className="ml-1 w-4 h-4 text-green-500 animate-bounce" />
                )}
              </span>
            </div>
            <div className="relative w-full max-w-xs">
              <div className="h-2 sm:h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    viewAwarded ? "bg-green-500" : "bg-blue-500"
                  }`}
                  style={{
                    width: `${Math.min(
                      100,
                      (viewProgress / photoTime) * 100
                    )}%`,
                  }}
                ></div>
              </div>
              <div className="text-center text-xs font-medium text-gray-700 dark:text-gray-200 mt-1">
                {viewAwarded
                  ? `+${photoPoints} pts awarded!`
                  : `${Math.min(photoTime, viewProgress)} / ${photoTime} sec`}
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-4 sm:gap-6">
          <div className="flex items-center justify-center">
            <img
              src={mediaItem.photoURL}
              alt="Full-size media"
              className="w-full h-auto max-h-[50vh] sm:max-h-[60vh] object-contain rounded-lg"
            />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center mb-3 sm:mb-4">
              {uploader.profilePic ? (
                <img
                  src={uploader.profilePic}
                  alt={uploader.username}
                  className="w-8 sm:w-10 h-8 sm:h-10 rounded-full object-cover mr-2 sm:mr-3"
                />
              ) : (
                <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-full bg-gray-300 flex items-center justify-center mr-2 sm:mr-3 text-sm sm:text-base font-bold text-white">
                  {uploader.username?.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <div className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-100">
                  {uploader.username}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {mediaItem.category}
                </div>
              </div>
            </div>
            <div className="mb-2 sm:mb-3">
              <div className="text-base sm:text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">
                {mediaItem.title}
              </div>
              {mediaItem.description && (
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  {mediaItem.description}
                </div>
              )}
            </div>
            <div className="flex items-center gap-4 sm:gap-6 mb-3 sm:mb-4">
              <button
                onClick={onLike}
                className={`flex items-center gap-1 text-pink-600 hover:text-pink-700 text-sm sm:text-base font-semibold ${
                  hasLiked ? "font-bold" : ""
                }`}
              >
                ❤️ <span>{likes}</span>
              </button>
              <div className="flex items-center gap-1 text-blue-600 text-sm sm:text-base font-semibold">
                💬 <span>{comments.length}</span>
              </div>
              <button
                onClick={onShare}
                className="flex items-center gap-1 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 text-sm sm:text-base font-semibold"
              >
                <FiShare2 /> Share
              </button>
            </div>
            <div className="flex-1 overflow-y-auto mb-2 max-h-32 sm:max-h-48">
              {comments.length === 0 ? (
                <div className="text-gray-400 text-xs sm:text-sm">
                  No comments yet.
                </div>
              ) : (
                comments.map((c, idx) => (
                  <div key={idx} className="mb-2 flex items-start gap-2">
                    <div className="w-6 sm:w-8 h-6 sm:h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-700">
                      {c.username?.charAt(0).toUpperCase()}
                    </div>
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-lg px-2 sm:px-3 py-1">
                      <div className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                        {c.username}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                        {c.text}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <form onSubmit={handleCommentSubmit} className="flex gap-2 mt-2">
              <input
                type="text"
                className="flex-1 border border-gray-300 rounded-full px-3 py-1 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                placeholder="Add a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                disabled={!currentUser}
              />
              <button
                type="submit"
                className="bg-blue-600 text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-semibold disabled:opacity-50 hover:bg-blue-700 transition-colors duration-200"
                disabled={!commentText.trim() || !currentUser}
              >
                Post
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

// Event Modal component
const EventModal = ({ event, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-slideUp p-2 sm:p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-4 w-full max-w-3xl h-[90vh] sm:h-auto overflow-y-auto relative flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 p-2"
          aria-label="Close modal"
        >
          <FiX className="text-2xl sm:text-3xl" />
        </button>
        <div className="flex flex-col gap-4 sm:gap-6">
          <div className="flex items-center justify-center">
            <img
              src={event.imageUrl}
              alt={event.title}
              className="w-full h-auto max-h-[50vh] sm:max-h-[60vh] object-contain rounded-lg"
              onError={(e) => {
                e.target.src =
                  "https://via.placeholder.com/320x180?text=No+Image";
              }}
            />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center mb-3 sm:mb-4">
              <FiCalendar className="text-blue-600 dark:text-blue-400 text-xl sm:text-2xl mr-2 sm:mr-3" />
              <div>
                <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {event.title}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Event
                </div>
              </div>
            </div>
            <div className="mb-2">
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2">
                {event.description}
              </div>
              <div className="flex items-center text-xs text-gray-500 dark:text-gray-500">
                <FiCalendar className="mr-1" />
                <span>{new Date(event.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const [media, setMedia] = useState([]);
  const [allMediaData, setAllMedia] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingData, setLoading] = useState(true);
  const [err, setError] = useState("");
  const [usersProfiles, setUserProfiles] = useState({});
  const [menuOpen, setMenuOpen] = useState(null);
  const [isMuted, setIsMuted] = useState({});
  const [videoDurationTimes, setVideoDurations] = useState({});
  const [selectedMediaType, setSelectedMediaType] = useState("all");
  const [mediaModal, setMediaModal] = useState({
    isOpen: false,
    mediaUrl: null,
  });
  const [eventModal, setEventModal] = useState({ isOpen: false, event: null });
  const videoReference = useRef({});
  const navigate = useNavigate();
  const {
    searchQuery,
    isSearching,
    clearSearch,
    externalEmbed,
    clearEmbedType,
    youtubeResults,
    updateYoutubeResults,
  } = useSearch();
  const [photoLikes, setPhotoLikes] = useState({});
  const [photoComments, setPhotoComments] = useState({});
  const [hasLikedPhoto, setHasLikedPhoto] = useState({});
  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const [iframeLoading, setIframeLoading] = useState(true);
  const [youtubeCache, setYoutubeCache] = useState({});
  const [commissionConfig, setCommissionConfig] = useState({
    photoTime: 0,
    photoPoints: 0,
    googleTime: 0,
    googlePoints: 0,
  });
  const [giftConfig, setGiftConfig] = useState({
    likePoints: 0,
    sharePoints: 0,
  });
  const [photoViewProgress, setPhotoViewProgress] = useState({});
  const [photoViewAwarded, setPhotoViewAwarded] = useState({});
  const [showPhotoToast, setShowPhotoToast] = useState(null);
  const [googleViewProgress, setGoogleViewProgress] = useState(0);
  const [googleTotalWatchTime, setGoogleTotalWatchTime] = useState(0);
  const [googleRewardCount, setGoogleRewardCount] = useState(0);
  const [showGoogleToast, setShowGoogleToast] = useState(null);
  const [googleRewarded, setGoogleRewarded] = useState(false);
  const [googleSearchQuery, setGoogleSearchQuery] = useState("");
  const [googleSuggestions, setGoogleSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [allUsersPoints, setAllUsersPoints] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sliderRef = useRef(null);
  const [heading, setHeading] = useState('');

  // Function to generate avatar with initials
  const generateAvatarImage = (name, size = 40) => {
    const colorsArray = [
      "#FFB900",
      "#FF8C00",
      "#F7630C",
      "#CA5010",
      "#DA3B01",
      "#EF6950",
      "#D13438",
      "#FF4343",
      "#E74856",
      "#E81123",
      "#EA005E",
      "#C30052",
      "#E3008C",
      "#BF0077",
      "#C239B3",
      "#9A0089",
      "#0078D7",
      "#0063B1",
      "#8E8CD8",
      "#6B69D6",
      "#8764B8",
      "#744DA9",
      "#B146C2",
      "#881798",
      "#0099BC",
    ];

    const char = name.charAt(0).toUpperCase();
    const color = colorsArray[char.charCodeAt(0) % colorsArray.length];

    return (
      <div
        className="flex items-center justify-center rounded-full text-white font-medium"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          backgroundColor: color,
          fontSize: `${size * 0.5}px`,
        }}
      >
        {char}
      </div>
    );
  };

  // Function to calculate relative time
  const getRelativeTimeAgo = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60)
      return `${diffInSeconds} second${diffInSeconds !== 1 ? "s" : ""} ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60)
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30)
      return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12)
      return `${diffInMonths} month${diffInMonths !== 1 ? "s" : ""} ago`;
    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} year${diffInYears !== 1 ? "s" : ""} ago`;
  };

  // Function to format duration
  const formatDurationTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    }
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  // Function to generate a unique media ID
  const generateMediaIdUnique = (mobile, timestamp, mediaType) => {
    const sanitizedMobile = mobile.replace(/[^0-9]/g, "_");
    const timeStr = timestamp.getTime().toString(36);
    return `${sanitizedMobile}_${mediaType}_${timeStr}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
  };

  // Function to filter media based on search query and category
  const filterMediaItems = (mediaItems, query, mediaType) => {
    let filteredItems = [];
    if (mediaType === "photo") {
      filteredItems = mediaItems.filter((item) => item.type === "photo");
    } else if (mediaType === "video") {
      filteredItems = mediaItems.filter((item) => item.type === "video");
    } else if (mediaType === "all") {
      filteredItems = mediaItems.filter(
        (item) => item.type === "photo" || item.type === "video"
      );
    } else {
      filteredItems = [];
    }
    if (query && query.trim()) {
      const searchTermLower = query.toLowerCase().trim();
      filteredItems = filteredItems.filter((item) => {
        const titleLower = item.title.toLowerCase();
        const descriptionLower = item.description
          ? item.description.toLowerCase()
          : "";
        const categoryLower = item.category.toLowerCase();
        const channelMobileLower = item.channelMobile.toLowerCase();
        return (
          titleLower.includes(searchTermLower) ||
          descriptionLower.includes(searchTermLower) ||
          categoryLower.includes(searchTermLower) ||
          channelMobileLower.includes(searchTermLower)
        );
      });
    }
    // console.log(
    //   "Filter:",
    //   mediaType,
    //   filteredItems.map((i) => ({ type: i.type, title: i.title }))
    // );
    return filteredItems;
  };

  // Fetch user profiles
  const fetchUserProfilesData = async (mobiles) => {
    try {
      const profilesData = {};
      for (const mobile of mobiles) {
        const sanitizedMobile = mobile.replace(/[^0-9]/g, "_");
        const profileRefPath = ref(
          database,
          `videoplayer/UserProfile/${sanitizedMobile}`
        );
        const usernameRefPath = ref(
          database,
          `videoplayer/Users/${sanitizedMobile}/username`
        );

        const [profileSnapshotData, usernameSnapshotData] = await Promise.all([
          get(profileRefPath),
          get(usernameRefPath),
        ]);

        profilesData[mobile] = {
          profilePic: profileSnapshotData.exists()
            ? profileSnapshotData.val().profilePic
            : null,
          username: usernameSnapshotData.exists()
            ? usernameSnapshotData.val()
            : mobile,
        };
      }
      setUserProfiles(profilesData);
    } catch (err) {
      // console.error("Error fetching user profiles:", err);
    }
  };

  // Fetch events from Firebase
  useEffect(() => {
    const eventsRef = ref(database, "videoplayer/Events");
    const unsubscribeEvents = onValue(
      eventsRef,
      (snapshot) => {
        const eventsData = snapshot.val();
        if (eventsData) {
          const eventsArray = Object.entries(eventsData).map(([id, event]) => ({
            id,
            title: event.title || "Untitled Event",
            description: event.description || "",
            imageUrl:
              event.imageUrl ||
              "https://via.placeholder.com/320x180?text=No+Image",
            createdAt: event.createdAt ? new Date(event.createdAt) : new Date(),
          }));
          eventsArray.sort((a, b) => b.createdAt - a.createdAt);
          setEvents(eventsArray);
        } else {
          setEvents([]);
        }
      },
      (error) => {
        // console.error("Error fetching events:", error);
        setError("Failed to load events. Please try again later.");
      }
    );

    return () => unsubscribeEvents();
  }, []);

  // Fetch all media data
  useEffect(() => {
    const fetchAllMediaData = async () => {
      try {
        const videosRefPath = ref(database, "videoplayer/videoUpload");
        const videosSnapshotData = await get(videosRefPath);
        const allMediaItems = [];

        if (videosSnapshotData.exists()) {
          const videoData = videosSnapshotData.val();
          // console.log("Fetched videoData:", videoData);
          Object.entries(videoData).forEach(([userMobile, userVideos]) => {
            // console.log("userMobile:", userMobile, "userVideos:", userVideos);
            const sanitizedMobile = userMobile.replace(/[_]/g, "");

            Object.entries(userVideos).forEach(([videoKeyId, video]) => {
              // console.log("videoKeyId:", videoKeyId, "video:", video);
              // Only include videos with status 'approved'
              if ((video.status || "").toLowerCase() !== "approved") return;
              const videoIdUnique =
                video.id ||
                generateMediaIdUnique(
                  sanitizedMobile,
                  new Date(video.uploadDate || Date.now()),
                  "video"
                );
              allMediaItems.push({
                id: videoIdUnique,
                dbKey: videoKeyId,
                type: "video",
                title: video.title || "Untitled Video",
                description: video.description || "",
                thumbnail:
                  video.thumbnailURL ||
                  "https://via.placeholder.com/320x180?text=No+Thumbnail",
                videoURL: video.videoURL || "",
                views: video.views || 0,
                uploadDate: video.uploadDate
                  ? new Date(video.uploadDate)
                  : new Date(),
                channelMobile: sanitizedMobile,
                privacy: video.privacy || "public",
                allowComments:
                  video.allowComments !== undefined
                    ? video.allowComments
                    : true,
                isForKids: video.isForKids || false,
                category: video.category || "Uncategorized",
                status: (video.status || "").toLowerCase(),
              });
            });
          });
        }

        const photosRefPath = ref(database, "videoplayer/photoUpload");
        const photosSnapshotData = await get(photosRefPath);

        if (photosSnapshotData.exists()) {
          const photoData = photosSnapshotData.val();

          Object.entries(photoData).forEach(([userMobile, userPhotos]) => {
            const sanitizedMobile = userMobile.replace(/[_]/g, "");

            Object.entries(userPhotos).forEach(([photoKeyId, photo]) => {
              if (photo.status !== "approved") return;

              const photoIdUnique =
                photo.id ||
                generateMediaIdUnique(
                  sanitizedMobile,
                  new Date(photo.uploadDate || Date.now()),
                  "photo"
                );

              allMediaItems.push({
                id: photoIdUnique,
                dbKey: photoKeyId,
                type: "photo",
                title: photo.title || "Untitled Photo",
                thumbnail:
                  photo.photoURL ||
                  "https://via.placeholder.com/320x180?text=No+Thumbnail",
                photoURL: photo.photoURL || "",
                uploadDate: photo.uploadDate
                  ? new Date(photo.uploadDate)
                  : new Date(),
                channelMobile: sanitizedMobile,
                category: photo.category || "Uncategorized",
              });
            });
          });
        }

        const uniqueMediaItems = Array.from(
          new Map(allMediaItems.map((item) => [item.id, item])).values()
        );

        uniqueMediaItems.sort((a, b) => b.uploadDate - a.uploadDate);

        setAllMedia(uniqueMediaItems);

        const initialMutedState = {};
        uniqueMediaItems
          .filter((item) => item.type === "video")
          .forEach((video) => {
            initialMutedState[video.id] = true;
          });
        setIsMuted(initialMutedState);

        const uniqueMobileNumbers = [
          ...new Set(uniqueMediaItems.map((item) => item.channelMobile)),
        ];
        await fetchUserProfilesData(uniqueMobileNumbers);

        const durationsData = {};
        await Promise.all(
          uniqueMediaItems
            .filter((item) => item.type === "video")
            .map(async (video) => {
              const videoElement = document.createElement("video");
              videoElement.src = video.videoURL;
              await new Promise((resolve) => {
                videoElement.onloadedmetadata = () => {
                  durationsData[video.id] = formatDurationTime(
                    videoElement.duration
                  );
                  resolve();
                };
                videoElement.onerror = () => {
                  durationsData[video.id] = "0:00";
                  resolve();
                };
              });
            })
        );
        setVideoDurations(durationsData);
      } catch (err) {
        // console.error("Error fetching media:", err);
        setError("Failed to load media. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllMediaData();
  }, []);

  // Effect to filter media when search query or media type changes
  useEffect(() => {
    if (isSearching) {
      let filteredMediaItems = filterMediaItems(
        allMediaData,
        searchQuery,
        selectedMediaType
      );
      if (
        selectedMediaType !== "event" &&
        youtubeResults &&
        youtubeResults.length > 0
      ) {
        if (selectedMediaType === "all" || selectedMediaType === "video") {
          filteredMediaItems = [
            ...filteredMediaItems,
            ...youtubeResults.map((v) => ({ ...v, type: "youtube_video" })),
          ];
        }
      }
      setMedia(filteredMediaItems);
    } else if (allMediaData.length > 0) {
      const filteredMediaItems = filterMediaItems(
        allMediaData,
        searchQuery,
        selectedMediaType
      );
      setMedia(filteredMediaItems);
    } else {
      setMedia([]);
    }
  }, [
    searchQuery,
    selectedMediaType,
    allMediaData,
    isSearching,
    youtubeResults,
  ]);

  const saveToHistoryRecord = async (video) => {
    const userData = JSON.parse(localStorage.getItem("currentUser"));
    if (!userData || !userData.mobile) {
      // console.log("No logged-in user found, skipping history save.");
      return;
    }

    const sanitizedMobile = userData.mobile.replace(/[^0-9]/g, "_");
    const sanitizedTitle = video.title
      .replace(/[^a-zA-Z0-9]/g, "_")
      .toLowerCase();
    const historyIdUnique = `${sanitizedMobile}_${sanitizedTitle}`;
    const historyRefPath = ref(
      database,
      `videoplayer/history/${sanitizedMobile}/${historyIdUnique}`
    );

    try {
      await set(historyRefPath, {
        videoId: video.id,
        title: video.title,
        thumbnailURL: video.thumbnail,
        videoURL: video.videoURL,
        timestamp: serverTimestamp(),
      });
    } catch (err) {
      // console.error("Error saving to history:", err);
    }
  };

  const toggleMenuVisibility = (mediaId) => {
    setMenuOpen(menuOpen === mediaId ? null : mediaId);
  };

  const handleDownloadAction = (mediaUrl, title) => {
    const a = document.createElement("a");
    a.href = mediaUrl;
    a.download = title || "media";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleShareAction = (mediaId) => {
    const mediaItem = media.find((m) => m.id === mediaId);
    if (!mediaItem) return;

    const shareUrl = `${window.location.origin}/${mediaItem.type}/${mediaId}`;
    if (navigator.share) {
      navigator
        .share({
          title: mediaItem.title || `Check out this ${mediaItem.type}`,
          url: shareUrl,
        })
        .catch(console.error);
    } else {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          alert("Link copied to clipboard!");
        })
        .catch(() => {
          prompt("Copy this link:", shareUrl);
        });
    }
  };

  const handleEditAction = (mediaId) => {
    navigate(`/edit/${mediaId}`);
  };

  const handleDeleteAction = async (mediaId) => {
    const mediaItem = media.find((m) => m.id === mediaId);
    if (!mediaItem) return;

    if (
      window.confirm(`Are you sure you want to delete this ${mediaItem.type}?`)
    ) {
      try {
        const sanitizedMobile = mediaItem.channelMobile.replace(/[^0-9]/g, "_");
        const mediaRefPath = ref(
          database,
          `videoplayer/${mediaItem.type}Upload/${sanitizedMobile}/${mediaItem.dbKey}`
        );
        await set(mediaRefPath, null);
        setMedia(media.filter((m) => m.id !== mediaId));
        setAllMedia(allMediaData.filter((m) => m.id !== mediaId));
        setMenuOpen(null);
      } catch (err) {
        // console.error(`Error deleting ${mediaItem.type}:`, err);
        alert(`Failed to delete ${mediaItem.type}. Please try again.`);
      }
    }
  };

  const handleMediaClickAction = (mediaId) => {
    const mediaItem = media.find((m) => m.id === mediaId);
    if (!mediaItem) return;
    // console.log("Photo clicked:", mediaItem);
    if (mediaItem.type === "video") {
      saveToHistoryRecord(mediaItem);
      navigate(`/video/${mediaId}`);
    } else if (mediaItem.type === "photo") {
      // Navigate to Photoshow page for photos
      navigate(`/photoshow/${mediaId}`, { state: { photo: mediaItem } });
    } else {
      // fallback for other types
      setMediaModal({ isOpen: true, mediaUrl: mediaItem.photoURL });
    }
  };

  const handleEventClick = (event) => {
    setEventModal({ isOpen: true, event });
  };

  const toggleMuteSound = (videoId) => {
    setIsMuted((prev) => {
      const newMutedState = { ...prev, [videoId]: !prev[videoId] };
      if (videoReference.current[videoId]) {
        videoReference.current[videoId].muted = newMutedState[videoId];
      }
      return newMutedState;
    });
  };

  const clearSearchQuery = () => {
    clearSearch();
  };

  const handleMediaTypeChange = (mediaType) => {
    setSelectedMediaType(mediaType);
  };

  const closeMediaModalWindow = () => {
    setMediaModal({ isOpen: false, mediaUrl: null });
  };

  const closeEventModal = () => {
    setEventModal({ isOpen: false, event: null });
  };

  // Fetch likes/comments for photos when modal opens
  useEffect(() => {
    if (mediaModal.isOpen && mediaModal.mediaItem) {
      const photo = media.find((m) => m.id === mediaModal.mediaItem.id);
      if (!photo) return;
      const sanitizedMobile = photo.channelMobile.replace(/[^0-9]/g, "_");
      const photoRefPath = ref(
        database,
        `videoplayer/photoUpload/${sanitizedMobile}/${photo.dbKey}`
      );
      get(
        ref(
          database,
          `videoplayer/photoUpload/${sanitizedMobile}/${photo.dbKey}/likes`
        )
      ).then((snapshot) => {
        const likesObj = snapshot.exists() ? snapshot.val() : {};
        setPhotoLikes((prev) => ({
          ...prev,
          [photo.id]: Object.keys(likesObj || {}),
        }));
        setHasLikedPhoto((prev) => ({
          ...prev,
          [photo.id]: !!(
            currentUser &&
            likesObj &&
            likesObj[currentUser.mobile]
          ),
        }));
      });
      get(
        ref(
          database,
          `videoplayer/photoUpload/${sanitizedMobile}/${photo.dbKey}/comments`
        )
      ).then((snapshot) => {
        const commentsArr = snapshot.exists()
          ? Object.values(snapshot.val())
          : [];
        setPhotoComments((prev) => ({ ...prev, [photo.id]: commentsArr }));
      });
    }
  }, [mediaModal, media, currentUser]);

  // Like handler
  const handleLikePhoto = async (photo) => {
    if (!currentUser) return;
    const sanitizedMobile = photo.channelMobile.replace(/[^0-9]/g, "_");
    const likeRef = ref(
      database,
      `videoplayer/photoUpload/${sanitizedMobile}/${photo.dbKey}/likes/${currentUser.mobile}`
    );
    const alreadyLiked = hasLikedPhoto[photo.id];
    await set(likeRef, alreadyLiked ? null : true);
    setHasLikedPhoto((prev) => ({ ...prev, [photo.id]: !alreadyLiked }));
    setPhotoLikes((prev) => ({
      ...prev,
      [photo.id]: alreadyLiked
        ? (prev[photo.id] || []).filter((m) => m !== currentUser.mobile)
        : [...(prev[photo.id] || []), currentUser.mobile],
    }));
    if (!alreadyLiked) {
      awardPoints(giftConfig.likePoints || 0, "like", "Photo Like Reward");
    }
  };

  // Comment handler
  const handleCommentPhoto = async (photo, text) => {
    if (!currentUser) return;
    const sanitizedMobile = photo.channelMobile.replace(/[^0-9]/g, "_");
    const commentRef = ref(
      database,
      `videoplayer/photoUpload/${sanitizedMobile}/${photo.dbKey}/comments`
    );
    const newComment = {
      username: currentUser.username || currentUser.mobile,
      text,
    };
    const commentsSnapshot = await get(commentRef);
    let commentsArr = commentsSnapshot.exists()
      ? Object.values(commentsSnapshot.val())
      : [];
    commentsArr.push(newComment);
    await set(commentRef, commentsArr);
    setPhotoComments((prev) => ({ ...prev, [photo.id]: commentsArr }));
  };

  // Share handler for modal
  const handleSharePhoto = (photo) => {
    const shareUrl = `${window.location.origin}/photo/${photo.id}`;
    if (navigator.share) {
      navigator
        .share({ title: photo.title, url: shareUrl })
        .catch(console.error);
    } else {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          alert("Link copied to clipboard!");
        })
        .catch(() => {
          prompt("Copy this link:", shareUrl);
        });
    }
    awardPoints(giftConfig.sharePoints || 0, "share", "Photo Share Reward");
  };

  const handleAddToPlaylist = async (video) => {
    if (!currentUser) {
      alert("You must be logged in to add to a playlist.");
      return;
    }
    const playlistName = prompt("Enter playlist name:");
    if (!playlistName || !playlistName.trim()) return;
    const sanitizedMobile = currentUser.mobile.replace(/[^0-9]/g, "_");
    const playlistRef = ref(
      database,
      `videoplayer/playlist/${sanitizedMobile}/${playlistName.trim()}`
    );
    try {
      await set(playlistRef, {
        ...(video || {}),
        addedAt: new Date().toISOString(),
      });
      alert("Video added to playlist!");
    } catch (err) {
      alert("Failed to add to playlist.");
      // console.error(err);
    }
  };

  useEffect(() => {
    if (externalEmbed === "google" || externalEmbed === "youtube") {
      setIframeLoading(true);
    }
  }, [externalEmbed]);

  // Fetch trending YouTube videos when externalEmbed is 'youtube'
  useEffect(() => {
    if (externalEmbed === "youtube" && youtubeResults.length === 0) {
      (async () => {
        const cacheKey = "trending_music_12";
        const cacheExpiry = 30 * 60 * 1000;

        if (
          youtubeCache[cacheKey] &&
          Date.now() - youtubeCache[cacheKey].timestamp < cacheExpiry
        ) {
          updateYoutubeResults(youtubeCache[cacheKey].data);
          return;
        }

        const results = await searchYouTubeVideos("trending music", 12);

        setYoutubeCache((prev) => ({
          ...prev,
          [cacheKey]: {
            data: results,
            timestamp: Date.now(),
          },
        }));

        updateYoutubeResults(results);
      })();
    }
  }, [
    externalEmbed,
    youtubeResults.length,
    updateYoutubeResults,
    youtubeCache,
  ]);

  // Fetch commission/gift configs on mount
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const commissionRef = ref(database, "videoplayer/Commissions/1");
        const giftRef = ref(database, "videoplayer/Gifts/1");
        const [commissionSnap, giftSnap] = await Promise.all([
          get(commissionRef),
          get(giftRef),
        ]);
        if (commissionSnap.exists()) setCommissionConfig(commissionSnap.val());
        if (giftSnap.exists()) setGiftConfig(giftSnap.val());
      } catch (err) {
        // console.error("Error fetching commission/gift configs:", err);
      }
    };
    fetchConfigs();
  }, []);

  // Award points helper
  const awardPoints = async (points, type, giftName) => {
    if (!points || points <= 0 || !currentUser) return;
    const sanitizedMobile = currentUser.mobile.replace(/[^0-9]/g, "_");
    const userPointsRef = ref(database, `videoplayer/Users/${sanitizedMobile}`);
    try {
      const pointsSnapshot = await get(userPointsRef);
      const currentPoints = pointsSnapshot.exists()
        ? pointsSnapshot.val().points || 0
        : 0;
      const newPoints = currentPoints + points;
      await set(userPointsRef, { ...pointsSnapshot.val(), points: newPoints });

      // Only add to gifts if it's not a Google view reward
      if (type !== "google_view") {
        const userGiftsRef = ref(
          database,
          `videoplayer/Users/${sanitizedMobile}/gifts`
        );
        const giftData = {
          giftName,
          points,
          assignedAt: new Date().toISOString(),
          type,
        };
        const newGiftKey = Math.random().toString(36).slice(2, 10);
        await set(
          ref(
            database,
            `videoplayer/Users/${sanitizedMobile}/gifts/${newGiftKey}`
          ),
          giftData
        );
      }

      setShowPhotoToast({ points, type });
      setTimeout(() => setShowPhotoToast(null), 2000);
    } catch (err) {
      // console.error(`Error awarding ${points} points for ${type}:`, err);
    }
  };

  // Add handler for photo view progress and awarding points
  const handlePhotoViewAward = (photo, force = false) => {
    if (!photo || !currentUser) return;
    const id = photo.id;
    if (photoViewAwarded[id]) return;
    setPhotoViewProgress((prev) => {
      const next = (prev[id] || 0) + 1;
      if (
        (next >= (commissionConfig.photoTime || 0) || force) &&
        !photoViewAwarded[id]
      ) {
        setPhotoViewAwarded((pv) => ({ ...pv, [id]: true }));
        awardPoints(
          commissionConfig.photoPoints || 0,
          "photo_view",
          "Photo View Reward"
        );
      }
      return { ...prev, [id]: next };
    });
  };

  // Google view progress and awarding points (repeating interval)
  useEffect(() => {
    if (
      externalEmbed !== "google" ||
      !currentUser ||
      !commissionConfig.googleTime
    )
      return;

    const googleTimeSeconds = commissionConfig.googleTime * 60; // Convert minutes to seconds
    if (googleTimeSeconds <= 0) return;

    let interval = setInterval(() => {
      setGoogleTotalWatchTime((prev) => prev + 1);
      setGoogleViewProgress((prev) => {
        const next = prev + 1;
        if (next >= googleTimeSeconds) {
          // Award points for each interval
          setGoogleRewardCount((count) => count + 1);
          awardPoints(
            commissionConfig.googlePoints || 0,
            "google_view",
            "Google View Reward"
          );
          setShowGoogleToast({
            points: commissionConfig.googlePoints || 0,
            type: "google_view",
          });
          setTimeout(() => setShowGoogleToast(null), 2000);
          return 0; // Reset for next interval
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [
    externalEmbed,
    currentUser,
    commissionConfig.googleTime,
    commissionConfig.googlePoints,
  ]);

  // Reset Google progress when Google embed is closed
  useEffect(() => {
    if (externalEmbed !== "google") {
      setGoogleViewProgress(0);
      setGoogleTotalWatchTime(0);
      setGoogleRewardCount(0);
    }
  }, [externalEmbed]);

  // Debounced search suggestions for Google
  useEffect(() => {
    if (externalEmbed === "google" && googleSearchQuery.trim()) {
      const timeoutId = setTimeout(() => {
        const fetchGoogleSuggestions = async (query) => {
          if (!query.trim()) {
            setGoogleSuggestions([]);
            setShowSuggestions(false);
            setLoadingSuggestions(false);
            return;
          }

          setLoadingSuggestions(true);
          try {
            // Try multiple Google suggestion APIs for better reliability
            const apis = [
              `https://suggestqueries.google.com/complete/search?client=firefox&q=${encodeURIComponent(
                query
              )}`,
              `https://suggestqueries.google.com/complete/search?client=chrome&q=${encodeURIComponent(
                query
              )}`,
              `https://suggestqueries.google.com/complete/search?client=opera&q=${encodeURIComponent(
                query
              )}`,
            ];

            let suggestions = [];
            for (const api of apis) {
              try {
                const response = await fetch(api, {
                  method: "GET",
                  headers: {
                    Accept: "application/json",
                  },
                });

                if (response.ok) {
                  const data = await response.json();
                  if (data && data[1] && data[1].length > 0) {
                    suggestions = data[1].slice(0, 8);
                    break; // Use the first successful API response
                  }
                }
              } catch (error) {
                // console.log("API attempt failed:", api, error);
                continue; // Try next API
              }
            }

            // If no suggestions from APIs, create some basic suggestions
            if (suggestions.length === 0) {
              suggestions = [
                `${query} meaning`,
                `${query} definition`,
                `${query} examples`,
                `${query} tutorial`,
                `${query} guide`,
                `${query} how to`,
                `${query} tips`,
                `${query} best practices`,
              ];
            }

            setGoogleSuggestions(suggestions);
            setShowSuggestions(true);
            setLoadingSuggestions(false);
            // console.log("Suggestions loaded:", suggestions);
          } catch (error) {
            // console.log("Error fetching suggestions:", error);
            // Fallback suggestions
            const fallbackSuggestions = [
              `${query} meaning`,
              `${query} definition`,
              `${query} examples`,
              `${query} tutorial`,
            ];
            setGoogleSuggestions(fallbackSuggestions);
            setShowSuggestions(true);
            setLoadingSuggestions(false);
          }
        };

        fetchGoogleSuggestions(googleSearchQuery);
      }, 300);

      return () => clearTimeout(timeoutId);
    } else if (externalEmbed !== "google") {
      setGoogleSuggestions([]);
      setShowSuggestions(false);
    }
  }, [googleSearchQuery, externalEmbed]);

  // Fetch all users' points for the slider
  // useEffect(() => {
  //   const fetchAllUsersPoints = async () => {
  //     try {
  //       const usersRef = ref(database, 'videoplayer/Users');
  //       const snapshot = await get(usersRef);
  //       if (snapshot.exists()) {
  //         const usersData = snapshot.val();
  //         const usersArray = Object.entries(usersData)
  //           .filter(([id, user]) => (user.status || '').toLowerCase() === 'accepted')
  //           .map(([id, user]) => ({
  //             mobile: user.mobile || user.phoneNumber || id,
  //             points: user.points || 0,
  //             time: user.timestamp || user.createdAt || user.registered || user.time || 'N/A',
  //           }));
  //         setAllUsersPoints(usersArray);
  //       } else {
  //         setAllUsersPoints([]);
  //       }
  //     } catch (err) {
  //       setAllUsersPoints([]);
  //     }
  //   };
  //   fetchAllUsersPoints();
  // }, []);


// Fetch all users' withdrawn points for the slider (sum all withdrawals per user)
useEffect(() => {
  const fetchAllUsersWithdrawPoints = async () => {
    try {
      const withdrawRef = ref(database, 'videoplayer/withdraw');
      const snapshot = await get(withdrawRef);
      if (snapshot.exists()) {
        const withdrawData = snapshot.val();
        // withdrawData: { userMobile: { seq: {withdrawPoints, timestamp, ...}, ... }, ... }
        const usersArray = Object.entries(withdrawData)
          .map(([mobile, requests]) => {
            // requests: { seq: {withdrawPoints, timestamp, ...}, ... }
            const reqArr = Object.values(requests || {});
            if (reqArr.length === 0) return null;
            // Sum all withdrawPoints for this user
            const totalWithdrawn = reqArr.reduce((sum, req) => sum + (parseInt(req.withdrawPoints, 10) || 0), 0);
            // Find latest timestamp among all withdrawals
            let latestTime = null;
            reqArr.forEach((req) => {
              if (req.timestamp) {
                const t = new Date(req.timestamp).getTime();
                if (!latestTime || t > latestTime) latestTime = t;
              }
            });
            return {
              mobile,
              points: totalWithdrawn,
              time: latestTime || 'N/A',
            };
          })
          .filter(Boolean)
          .filter((user) => user.points > 0); // Only show users with withdrawals
        // Sort by points descending, then by latest time
        usersArray.sort((a, b) => b.points - a.points || (b.time || 0) - (a.time || 0));
        setAllUsersPoints(usersArray);
      } else {
        setAllUsersPoints([]);
      }
    } catch (err) {
      setAllUsersPoints([]);
    }
  };
  fetchAllUsersWithdrawPoints();
}, []);

  // Auto-scroll effect for slider
  useEffect(() => {
    if (allUsersPoints.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % allUsersPoints.length);
    }, 60000);
    return () => clearInterval(interval);
  }, [allUsersPoints]);

  // Scroll to current index
  useEffect(() => {
    if (!sliderRef.current || allUsersPoints.length === 0) return;
    const slider = sliderRef.current;
    const child = slider.children[currentIndex];
    if (!child) return;

    // Check if slider is in viewport
    const sliderRect = slider.getBoundingClientRect();
    const inViewport =
      sliderRect.top >= 0 &&
      sliderRect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

    if (inViewport) {
      child.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [currentIndex, allUsersPoints]);

  // Helper to format date
  function formatDateTime(dateStringOrMillis) {
    if (!dateStringOrMillis || dateStringOrMillis === 'N/A') return 'No Time';
    let dateObj;
    if (typeof dateStringOrMillis === 'number') {
      dateObj = new Date(dateStringOrMillis);
    } else if (!isNaN(Number(dateStringOrMillis))) {
      dateObj = new Date(Number(dateStringOrMillis));
    } else {
      dateObj = new Date(dateStringOrMillis);
    }
    if (isNaN(dateObj.getTime())) return 'No Time';
    return dateObj.toLocaleString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  }

  // Helper to format mobile number
  function formatMobile(mobile) {
    if (!mobile) return '';
    const digits = mobile.replace(/\D/g, '');
    if (digits.length === 10) {
      return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }
    return mobile;
  }

  // Fetch heading from Firebase
  useEffect(() => {
    const headingRef = ref(database, 'videoplayer/heading');
    const unsubscribe = onValue(headingRef, (snapshot) => {
      const data = snapshot.val();
      setHeading(data || '');
    });
    return () => unsubscribe();
  }, []);

  if (externalEmbed === "google") {
    const handleGoogleSearch = (e) => {
      e.preventDefault();
      if (googleSearchQuery.trim()) {
        const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(
          googleSearchQuery.trim()
        )}`;
        window.open(searchUrl, "_blank");
        setGoogleSearchQuery("");
        setShowSuggestions(false);
      }
    };

    // Handle suggestion click
    const handleSuggestionClick = (suggestion) => {
      const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(
        suggestion
      )}`;
      window.open(searchUrl, "_blank");
      setGoogleSearchQuery(suggestion);
      setShowSuggestions(false);
    };

    return (
      <div className="flex h-[calc(100vh-4rem)] min-h-[calc(100vh-4rem)] bg-gray-50 dark:bg-gray-900">
        <main className="flex-1 relative p-0 m-0 h-full min-h-[calc(100vh-4rem)]">
          <button
            onClick={clearEmbedType}
            className="absolute top-2 sm:top-4 right-2 sm:right-4 z-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-full p-2 sm:p-3 shadow hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Close"
          >
            <FiX className="text-lg sm:text-xl text-gray-600 dark:text-gray-300" />
          </button>

          {/* Google Timeline */}
          {commissionConfig.googleTime > 0 && currentUser && (
            <div className="absolute top-16 sm:top-20 left-2 sm:left-4 z-20 w-[95vw] max-w-xs sm:max-w-xs">
              <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow flex flex-col gap-2 w-full">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <FiClock className="text-blue-500" size={18} />
                  <span>
                    Watched:{" "}
                    <span className="font-medium">
                      {formatDurationTime(googleTotalWatchTime)} / {formatDurationTime(commissionConfig.googleTime * 60)} min
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>
                    Total Time Spend On Google:{" "}
                    <span className="font-semibold text-blue-700 dark:text-blue-300">
                      {formatDurationTime(googleTotalWatchTime)}
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400">
                  <span>
                    Points per interval:{" "}
                    <span className="font-bold">
                      {commissionConfig.googlePoints}
                    </span>
                  </span>
                  {showGoogleToast && (
                    <div className="relative">
                      <div className="absolute -top-6 bg-blue-500 text-white text-xs rounded-full px-2 py-1 animate-bounce">
                        +{showGoogleToast.points} ({showGoogleToast.type.replace("_", " ")})
                      </div>
                    </div>
                  )}
                </div>
                <div className="w-full">
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-1000 ease-out"
                      style={{
                        width: `${Math.min(
                          100,
                          (googleViewProgress / (commissionConfig.googleTime * 60)) * 100
                        )}%`,
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>{googleViewProgress} sec</span>
                    <span>{commissionConfig.googleTime} min</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Google Search Interface */}
          <div className="flex flex-col items-center justify-center h-full min-h-[calc(100vh-4rem)] bg-white dark:bg-gray-900 px-2 sm:px-0">
            {/* Google Logo */}
            <div className="mb-8 flex items-center justify-center w-full">
              <div className="flex flex-row sm:flex-row items-end sm:items-center justify-center w-full flex-wrap sm:flex-nowrap">
                <div className="text-5xl xs:text-6xl sm:text-7xl md:text-9xl font-bold text-blue-600 dark:text-blue-400 leading-none">G</div>
                <div className="text-3xl xs:text-4xl sm:text-5xl md:text-7xl font-bold text-red-600 dark:text-red-400 leading-none">o</div>
                <div className="text-3xl xs:text-4xl sm:text-5xl md:text-7xl font-bold text-yellow-600 dark:text-yellow-400 leading-none">o</div>
                <div className="text-3xl xs:text-4xl sm:text-5xl md:text-7xl font-bold text-blue-600 dark:text-blue-400 leading-none">g</div>
                <div className="text-3xl xs:text-4xl sm:text-5xl md:text-7xl font-bold text-green-600 dark:text-green-400 leading-none">l</div>
                <div className="text-3xl xs:text-4xl sm:text-5xl md:text-7xl font-bold text-red-600 dark:text-red-400 leading-none">e</div>
              </div>
            </div>

            {/* Search Form */}
            <form
              onSubmit={handleGoogleSearch}
              className="w-full max-w-2xl mb-6 px-0 xs:px-2"
            >
              <div className="relative w-full">
                <div className="flex flex-row items-center w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-2xl shadow focus-within:border-blue-500 dark:focus-within:border-blue-400">
                  <div className="relative flex-1">
                    <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl pointer-events-none" />
                    <input
                      type="text"
                      value={googleSearchQuery}
                      onChange={(e) => {
                        setGoogleSearchQuery(e.target.value);
                        if (e.target.value.trim()) {
                          setShowSuggestions(true);
                        } else {
                          setShowSuggestions(false);
                        }
                      }}
                      onFocus={() => {
                        if (
                          googleSearchQuery.trim() &&
                          googleSuggestions.length > 0
                        ) {
                          setShowSuggestions(true);
                        }
                      }}
                      onBlur={() =>
                        setTimeout(() => setShowSuggestions(false), 200)
                      }
                      placeholder="Search Google or type a URL"
                      className="w-full h-12 pl-12 pr-4 text-base xs:text-lg bg-transparent border-none outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-2xl"
                    />
                  </div>
                  <button
                    type="submit"
                    className="h-12 px-6 xs:px-8 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-semibold transition-colors duration-200 shadow-none focus:outline-none"
                  >
                    Search
                  </button>
                </div>

                {/* Search Suggestions Dropdown */}
                {showSuggestions && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl z-50 max-h-60 xs:max-h-80 overflow-y-auto w-full min-w-0">
                    {loadingSuggestions ? (
                      <div className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
                        <span className="text-sm">Loading suggestions...</span>
                      </div>
                    ) : googleSuggestions.length > 0 ? (
                      <>
                        {googleSuggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="w-full text-left px-6 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center"
                          >
                            <FiSearch className="mr-3 text-gray-400 text-sm" />
                            <span className="text-gray-900 dark:text-gray-100">
                              {suggestion}
                            </span>
                          </button>
                        ))}
                      </>
                    ) : googleSearchQuery.trim() && !loadingSuggestions ? (
                      <div className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        <span className="text-sm">No suggestions found</span>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </form>

            {/* Google I'm Feeling Lucky Button */}
            <div className="flex flex-col xs:flex-row space-y-2 xs:space-y-0 xs:space-x-4 w-full max-w-2xl px-0 xs:px-2">
              <button
                onClick={() => {
                  const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(
                    googleSearchQuery.trim() || "I'm feeling lucky"
                  )}`;
                  window.open(searchUrl, "_blank");
                }}
                className="w-full xs:w-auto bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-6 py-3 rounded-full font-medium transition-colors duration-200"
              >
                I'm Feeling Lucky
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (loadingData) {
    return (
      <main className="flex-1 p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400 text-base sm:text-lg">
          Loading media...
        </div>
      </main>
    );
  }

  if (err) {
    return (
      <main className="flex-1 p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-red-600 dark:text-red-400 text-base sm:text-lg">
          Failed to load media. Please try again later.
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 p-4 sm:p-6 bg-gray-50 dark:bg-gray-900">
      {/* Top Heading Card */}
      <div className="mb-4">
        <div className="rounded-2xl shadow-lg bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 p-0.5 animate-fadeIn">
          <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-yellow-400 rounded-2xl p-2 sm:p-3 flex items-center justify-center relative overflow-hidden min-h-[48px]">
            <h1
              className="text-2xl sm:text-3xl font-extrabold text-white text-center drop-shadow-lg px-2 py-1 rounded-xl shadow-lg"
              style={{
                color: 'white',
              }}
            >
              {heading || 'Watch Videos And Earn Money'}
            </h1>
          </div>
        </div>
      </div>
      {/* Slider for all users' points */}
      <div className="w-full mb-6">
        <div className="overflow-x-auto whitespace-nowrap py-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <div
            className="flex space-x-3 px-2"
            ref={sliderRef}
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {allUsersPoints.length === 0 ? (
              <div className="text-gray-400 text-xs px-2">No user points data found.</div>
            ) : (
              allUsersPoints.map((user, idx) => (
                <div
                  key={user.mobile + idx}
                  className={`inline-block rounded-lg px-3 py-2 min-w-[160px] sm:min-w-[180px] border text-center transition-all duration-300
                    ${idx === currentIndex
                      ? 'bg-gradient-to-br from-blue-100 via-blue-200 to-blue-300 border-blue-400 shadow-lg ring-2 ring-blue-400'
                      : idx % 2 === 0
                        ? 'bg-white border-blue-100 shadow'
                        : 'bg-blue-50 border-blue-100 shadow-sm'}
                  `}
                  style={{ height: '72px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                >
                  <div className="font-semibold text-blue-700 text-base sm:text-lg leading-tight">{user.points.toLocaleString()} Points</div>
                  <div className="text-[11px] sm:text-xs text-gray-500 mt-0.5">{formatDateTime(user.time)}</div>
                  {/* <div className="text-xs text-gray-700 mt-0.5">{formatMobile(user.mobile)}</div> */}
                </div>
              ))
            )}
          </div>
        </div>
        {/* Hide scrollbar for all browsers */}
        <style>{`
          .overflow-x-auto::-webkit-scrollbar { display: none; }
        `}</style>
      </div>
      {/* Category Menu */}
      <div className="mb-4 sm:mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 overflow-x-auto">
        <nav className="flex space-x-4 sm:space-x-4 min-w-max">
          {[
            {
              id: "all",
              label: "All",
              icon: <FiGlobe className="mr-1 sm:mr-2 text-lg sm:text-xl" />,
            },
            {
              id: "photo",
              label: "Photos",
              icon: <FiImage className="mr-1 sm:mr-2 text-lg sm:text-xl" />,
            },
            {
              id: "video",
              label: "Videos",
              icon: <FiVideo className="mr-1 sm:mr-2 text-lg sm:text-xl" />,
            },
            {
              id: "event",
              label: "Events",
              icon: <FiCalendar className="mr-1 sm:mr-2 text-lg sm:text-xl" />,
            },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => handleMediaTypeChange(item.id)}
              className={`flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap ${
                selectedMediaType === item.id
                  ? "bg-blue-600 text-white shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              }`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Search Results Header */}
      {isSearching && searchQuery.trim() && selectedMediaType !== "event" && (
        <div className="mb-4 sm:mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <FiSearch className="text-blue-500 text-lg sm:text-xl" />
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Search Results
                </h2>
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  <p>
                    {media.length} item{media.length !== 1 ? "s" : ""} found for
                    "{searchQuery}"
                  </p>
                  {media.length > 0 && (
                    <div className="flex items-center space-x-2 sm:space-x-3 mt-1 sm:mt-0">
                      {media.some((item) => item.type === "video") && (
                        <span className="flex items-center text-green-600 dark:text-green-400">
                          <FiVideo className="mr-1 text-sm sm:text-base" />
                          {
                            media.filter((item) => item.type === "video").length
                          }{" "}
                          Videos
                        </span>
                      )}
                      {media.some((item) => item.type === "photo") && (
                        <span className="flex items-center text-blue-600 dark:text-blue-400">
                          <FiImage className="mr-1 text-sm sm:text-base" />
                          {
                            media.filter((item) => item.type === "photo").length
                          }{" "}
                          Photos
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={clearSearchQuery}
              className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200"
            >
              <FiX className="text-base sm:text-lg" />
              <span>Clear Search</span>
            </button>
          </div>
        </div>
      )}

      {/* Events Display */}
      {selectedMediaType === "event" ? (
        events.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 animate-fadeIn">
            <FiCalendar className="text-gray-400 text-3xl sm:text-4xl mb-3 sm:mb-4" />
            <div className="text-gray-600 dark:text-gray-400 text-base sm:text-lg">
              No events found
            </div>
            <p className="text-gray-500 dark:text-gray-500 text-xs sm:text-sm mt-1 sm:mt-2">
              Check back later for upcoming events
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {events.map((event, index) => (
              <div
                key={event.id}
                className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 ease-in-out transform hover:-translate-y-1 animate-slideUp"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => handleEventClick(event)}
              >
                <div className="relative group cursor-pointer">
                  <div className="relative pt-[56.25%]">
                    <img
                      src={event.imageUrl}
                      alt={event.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/320x180?text=No+Image";
                      }}
                    />
                    <div className="absolute top-1 sm:top-2 left-1 sm:left-2 bg-blue-600 text-white text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded flex items-center">
                      <FiCalendar className="mr-1 text-xs sm:text-sm" />
                      Event
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <div className="bg-blue-600 bg-opacity-80 rounded-full p-2 sm:p-3 transform group-hover:scale-110 transition-transform duration-200 shadow-lg">
                        <FiCalendar className="h-5 sm:h-6 w-5 sm:w-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-3 sm:p-4 flex flex-col">
                  <h3
                    className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-100 line-clamp-1 mb-1 sm:mb-2 transition-colors duration-200 hover:text-blue-600 dark:hover:text-blue-400"
                    title={event.title}
                  >
                    {event.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2 sm:mb-3">
                    {event.description}
                  </p>
                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-500">
                    <FiCalendar className="mr-1" />
                    <span>{getRelativeTimeAgo(event.createdAt)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : /* Media Display */
      media.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 animate-fadeIn">
          <FiFilm className="text-gray-400 text-3xl sm:text-4xl mb-3 sm:mb-4" />
          <div className="text-gray-600 dark:text-gray-400 text-base sm:text-lg">
            {isSearching && searchQuery.trim()
              ? `No items found for "${searchQuery}"`
              : `No ${
                  selectedMediaType === "all"
                    ? "media"
                    : selectedMediaType + "s"
                } found`}
          </div>
          <p className="text-gray-500 dark:text-gray-500 text-xs sm:text-sm mt-1 sm:mt-2">
            {isSearching && searchQuery.trim()
              ? "Try different keywords or browse all media"
              : "Upload a photo or video to get started"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {media.map((item, index) => {
            const userProfileData = usersProfiles[item.channelMobile] || {};
            const username =
              userProfileData.username && isNaN(userProfileData.username)
                ? userProfileData.username
                : "";
            const hasProfilePicImage =
              userProfileData.profilePic &&
              !userProfileData.profilePic.includes("placeholder.com");
            const isOwnerUser =
              JSON.parse(localStorage.getItem("currentUser"))?.mobile ===
              item.channelMobile;

            return (
              <div
                key={item.id}
                className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 ease-in-out transform hover:-translate-y-1 animate-slideUp"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div
                  className="relative group cursor-pointer"
                  onClick={() => {
                    if (item.type === "youtube_video") {
                      navigate(`/video/${item.id}`);
                    } else {
                      handleMediaClickAction(item.id);
                    }
                  }}
                >
                  <div className="relative pt-[56.25%]">
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/320x180?text=Thumbnail+Not+Found";
                      }}
                    />
                  </div>
                </div>
                <div className="p-3 sm:p-4 flex">
                  <div className="flex-shrink-0 mr-2 sm:mr-3">
                    {hasProfilePicImage ? (
                      <img
                        src={userProfileData.profilePic}
                        alt={username}
                        className="w-8 sm:w-9 h-8 sm:h-9 rounded-full object-cover hover:ring-2 hover:ring-blue-500 transition-all duration-300"
                        onError={(e) => {
                          e.target.parentNode.replaceChild(
                            generateAvatarImage(username, 32),
                            e.target
                          );
                        }}
                      />
                    ) : (
                      generateAvatarImage(username, 32)
                    )}
                  </div>
                  <div className="flex-grow min-w-0">
                    <h3
                      className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-100 line-clamp-1 mb-1 sm:mb-2 transition-colors duration-200 hover:text-blue-600 dark:hover:text-blue-400"
                      title={item.title}
                    >
                      {item.title}
                    </h3>
                    <div className="flex flex-col">
                      {username && (
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 cursor-pointer transition-colors duration-200">
                          {username}
                        </p>
                      )}
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-500">
                        {item.type === "video" && (
                          <>
                            <span>
                              {item.views?.toLocaleString() || 0} views
                            </span>
                            <span className="mx-1">•</span>
                          </>
                        )}
                        <span>{getRelativeTimeAgo(item.uploadDate)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="relative flex-shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMenuVisibility(item.id);
                      }}
                      className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none transition-colors duration-200 transform hover:rotate-90"
                    >
                      <FiMoreVertical className="text-gray-600 dark:text-gray-400 text-lg sm:text-xl" />
                    </button>
                    {menuOpen === item.id && (
                      <div className="absolute right-0 top-8 w-40 sm:w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl z-20 border border-gray-200 dark:border-gray-700 overflow-hidden animate-pulse">
                        <button
                          onClick={() => handleShareAction(item.id)}
                          className="w-full flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-600 transition-colors duration-200"
                        >
                          <FiShare2 className="mr-2 text-sm sm:text-base" />
                          Share
                        </button>
                        {item.type === "video" && (
                          <button
                            onClick={() => handleAddToPlaylist(item)}
                            className="w-full flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                          >
                            <FiFilm className="mr-2 text-sm sm:text-base" />
                            Add to Playlist
                          </button>
                        )}
                        {isOwnerUser && (
                          <button
                            onClick={() => handleDeleteAction(item.id)}
                            className="w-full flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                          >
                            <FiTrash2 className="mr-2 text-sm sm:text-base" />
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="px-3 sm:px-4 pb-3 sm:pb-4 mt-auto">
                  {item.type === "video" || item.type === "youtube_video" ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.type === "youtube_video") {
                          navigate(`/video/${item.id}`);
                        } else {
                          // console.log("Start earning by watching:", item.id);
                          handleMediaClickAction(item.id);
                        }
                      }}
                      className="w-full flex items-center justify-center relative overflow-hidden group h-10 sm:h-12"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg shadow-md group-hover:from-green-600 group-hover:to-emerald-700 transition-all duration-500 transform group-hover:scale-[1.02]"></div>
                      <div className="absolute inset-0 overflow-hidden rounded-lg">
                        <div className="absolute top-0 left-0 w-8 h-full bg-white opacity-30 -skew-x-12 animate-shine group-hover:left-[150%] transition-all duration-1000"></div>
                      </div>
                      <div className="relative z-10 flex items-center justify-center py-2 px-3 sm:px-4 w-full">
                        <div className="relative">
                          <FaRupeeSign className="mr-1 sm:mr-2 text-white text-base sm:text-lg animate-pulse" />
                          <div className="absolute -inset-1 bg-white rounded-full opacity-0 group-hover:opacity-20 group-hover:animate-ping-slow transition-opacity duration-300"></div>
                        </div>
                        <span className="text-white font-medium text-xs sm:text-sm drop-shadow-sm">
                          Watch & Earn
                        </span>
                      </div>
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMediaClickAction(item.id);
                      }}
                      className="w-full flex items-center justify-center py-2 px-3 sm:px-4 text-xs sm:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-300 h-10 sm:h-12"
                    >
                      <FiImage className="mr-1 sm:mr-2 text-base sm:text-lg" />
                      Show Image
                    </button>
                  )}
                  {item.type === "video" && (
                    <>
                      <div className="mt-2 h-1 sm:h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full animate-progress"
                          style={{ width: "100%" }}
                        ></div>
                      </div>
                      <div className="mt-1 sm:mt-2 flex items-center justify-center">
                        <div className="flex items-center bg-amber-100 dark:bg-amber-900 bg-opacity-50 px-1.5 sm:px-2 py-0.5 rounded-full animate-pulse-slow">
                          <FiCheck className="w-3 sm:w-4 h-3 sm:h-4 text-amber-500 dark:text-amber-300 mr-1" />
                          <span className="text-xs text-amber-700 dark:text-amber-200 font-medium">
                            Earning activated
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          <div className="mt-4 px-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              All rights reserved. © {new Date().getFullYear()}{" "}
              timepassservices.com
            </p>
          </div>
        </div>
      )}

      {mediaModal.isOpen &&
        mediaModal.mediaItem &&
        mediaModal.mediaItem.type === "photo" && (
          <MediaModal
            mediaItem={mediaModal.mediaItem}
            uploader={usersProfiles[mediaModal.mediaItem.channelMobile] || {}}
            onClose={() =>
              setMediaModal({ isOpen: false, mediaUrl: null, mediaItem: null })
            }
            currentUser={currentUser}
            onLike={() => handleLikePhoto(mediaModal.mediaItem)}
            onComment={(text) => handleCommentPhoto(mediaModal.mediaItem, text)}
            likes={photoLikes[mediaModal.mediaItem.id]?.length || 0}
            comments={photoComments[mediaModal.mediaItem.id] || []}
            hasLiked={hasLikedPhoto[mediaModal.mediaItem.id]}
            onShare={() => handleSharePhoto(mediaModal.mediaItem)}
            photoTime={commissionConfig.photoTime}
            photoPoints={commissionConfig.photoPoints}
            onPhotoViewAward={handlePhotoViewAward}
            viewProgress={photoViewProgress[mediaModal.mediaItem.id] || 0}
            viewAwarded={photoViewAwarded[mediaModal.mediaItem.id]}
          />
        )}

      {eventModal.isOpen && (
        <EventModal event={eventModal.event} onClose={closeEventModal} />
      )}

      {showPhotoToast && (
        <div className="fixed top-16 sm:top-20 right-2 sm:right-4 z-50 bg-green-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg shadow-lg animate-fadeIn text-xs sm:text-sm max-w-[80%] sm:max-w-md">
          +{showPhotoToast.points} points for{" "}
          {showPhotoToast.type.replace("_", " ")}!
        </div>
      )}

      {showGoogleToast && (
        <div className="fixed top-16 sm:top-20 right-2 sm:right-4 z-50 bg-green-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg shadow-lg animate-fadeIn text-xs sm:text-sm max-w-[80%] sm:max-w-md">
          +{showGoogleToast.points} points for{" "}
          {showGoogleToast.type.replace("_", " ")}!
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes shine {
          0% {
            left: -100%;
          }
          100% {
            left: 150%;
          }
        }
        @keyframes ping-slow {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          75%,
          100% {
            transform: scale(2);
            opacity: 0;
          }
        }
        @keyframes pulse-slow {
          0%,
          100% {
            opacity: 0.7;
          }
          50% {
            opacity: 1;
          }
        }
        @keyframes progress {
          0% {
            width: 0%;
          }
          100% {
            width: 100%;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.5s ease-out;
        }
        .animate-shine {
          animation: shine 2s linear infinite;
        }
        .animate-ping-slow {
          animation: ping-slow 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
        .animate-progress {
          animation: progress 10s linear infinite;
        }
        @keyframes gradientMove {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `}</style>
    </main>
  );
};

export default Home;

